/**
 * Changing what a line charges (F2).
 *
 * Parsing and the live markup↔charge arithmetic are pure so they can be tested;
 * the save is online only, like a quantity change, because the server decides
 * whether the line is still open to change (an invoice freezes it).
 */
import { changePrice } from "./actions";
import { cents, marginBp } from "./costing";
import { percent } from "./format";
import { sellFromMarkup } from "./pricing";

export const MAX_SELL_CENTS = 999_999_00;
export const PRICE_NEEDS_SIGNAL = "Changing a price needs a signal.";
const MAX_MARKUP_PERCENT = 100_000;

/** Dollars as typed → whole cents, or null if it isn't a sane charge. */
export function parseDollars(text: string): number | null {
  const t = text.trim().replace(/^\$/, "");
  if (!/^\d*\.?\d*$/.test(t) || t === "" || t === ".") return null;
  const value = cents(Number(t) * 100);
  return Number.isFinite(value) && value >= 0 && value <= MAX_SELL_CENTS ? value : null;
}

/** A markup percentage as typed → basis points, or null. */
export function parseMarkupBp(text: string): number | null {
  const t = text.trim().replace(/%$/, "");
  if (!/^\d*\.?\d*$/.test(t) || t === "" || t === ".") return null;
  const pct = Number(t);
  return Number.isFinite(pct) && pct >= 0 && pct <= MAX_MARKUP_PERCENT ? cents(pct * 100) : null;
}

/** The charge a typed markup gives on this cost, as a dollars string; null if the markup isn't a number. */
export function dollarsFromMarkup(unitCostCents: number, markupText: string): string | null {
  const bp = parseMarkupBp(markupText);
  if (bp === null) return null;
  return (sellFromMarkup(unitCostCents, bp) / 100).toFixed(2);
}

/** "Margin 28.6%" for a charge against a cost; null when there is no charge to take a margin of. */
export function marginHint(unitCostCents: number, sellCents: number | null): string | null {
  if (sellCents === null || sellCents <= 0) return null;
  return `Margin ${percent(marginBp(unitCostCents, sellCents))}`;
}

/**
 * Set a line's charge on the server and write the answer back to the device.
 * `reload()` alone only re-reads IndexedDB, which this call does not touch, so
 * the answer is put in the store here (as `actions.absorb` does for the other
 * online edits). Throws `ApiError`; status 0 means no signal.
 */
export async function setLinePrice(jobId: string, lineId: string, unitSellCents: number): Promise<void> {
  // Through the shared `absorb`, so the device's copy of the job, its lines and
  // its time entries all come from the one answer.
  await changePrice(jobId, lineId, unitSellCents);
}
